import type {
  UpsertUserSearchPreferencesMutation,
  UpsertUserSearchPreferencesMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

/**
 * Maps the tRPC mutation response to match the GraphQL mutation response shape
 * so consumers branching on the result do not need to know which path executed.
 */
const mapTrpcResponseToGraphQL = (data: {
  affected_rows: number;
}): UpsertUserSearchPreferencesMutation => ({
  insert_user_search_preferences: {
    __typename: 'user_search_preferences_mutation_response',
    affected_rows: data.affected_rows,
  },
});

export const useUpsertUserSearchPreferencesTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.userSearchPreferences.upsertUserSearchPreferences.mutationOptions(
      {
        onSuccess: async () => {
          // Mirror the legacy Apollo refetch of the search-preferences read.
          await queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.userSearchPreferences.getUserSearchPreferences.queryKey(),
          });
        },
      }
    ),
  });

  return {
    upsertUserSearchPreferences: async (
      input: UpsertUserSearchPreferencesMutationVariables
    ): Promise<UpsertUserSearchPreferencesMutation> => {
      const result = await mutation.mutateAsync({
        ShowGroups: input.ShowGroups,
        FilterByActivePlatformUsers: input.FilterByActivePlatformUsers,
        ShowUserPlatformRole: input.ShowUserPlatformRole,
        ShowUserJobTitle: input.ShowUserJobTitle,
        ShowDirectoryDepartment: input.ShowDirectoryDepartment,
        ShowUserLocation: input.ShowUserLocation,
        ShowUserEmail: input.ShowUserEmail,
        ShowArchivedUsers: input.ShowArchivedUsers,
        ShowInheritedContributors: input.ShowInheritedContributors,
      });

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
